/**
 * Collects the replacement text a recommendation needs to become an action.
 *
 * This is the missing half of "Create action": a deterministic check knows a
 * meta description is missing without knowing what it should say, which is why
 * the button used to fail with "does not carry enough detail to build an
 * executable change" on every finding the rule engine produced. The person
 * supplies the words, and the action then goes through the same approval,
 * execution and verification path as an AI-written one.
 */

import { useState } from 'react';

import { Button, Callout, Spinner } from '@/components/ui';
import { AlertIcon, InfoIcon, SparkIcon } from '@/components/icons';
import { inputClass } from '@/utils/format';
import { WRITABLE_ACTIONS, validateValue } from '@/features/opportunities/writableActions';
import type { DraftedValue } from '@/types/api';

const MAX_URLS = 10;

export function SupplyValuesForm({
  actionType,
  urls,
  drafts,
  draftNotes,
  drafting,
  onRedraft,
  pending,
  onCancel,
  onSubmit,
}: {
  actionType: string | null;
  urls: string[];
  /** Model-written starting points, keyed by URL. Always editable. */
  drafts?: Record<string, DraftedValue>;
  draftNotes?: string;
  drafting?: boolean;
  onRedraft?: () => void;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (payloads: Record<string, unknown>[]) => void;
}) {
  const spec = actionType ? WRITABLE_ACTIONS[actionType] : undefined;
  const shown = urls.slice(0, MAX_URLS);
  const hasDrafts = Boolean(drafts && Object.keys(drafts).length > 0);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // A draft is a starting value, not state: the field shows the edit if there
  // is one, otherwise whatever the model wrote, so a re-draft replaces text the
  // reviewer has not touched without discarding text they have.
  const valueFor = (url: string) => edits[url] ?? drafts?.[url]?.after ?? '';

  if (!spec) {
    return (
      <Callout tone="info" title="This one needs more than text" icon={<InfoIcon className="h-5 w-5" />}>
        {actionType
          ? `A ${actionType.replace(/_/g, ' ').toLowerCase()} change needs details a text box cannot carry — a link target, an image, or structured data. Run the AI analysis with Search Console connected, or apply this one yourself.`
          : 'This recommendation has no executable action type. Apply it yourself, then re-run the audit to confirm it landed.'}
      </Callout>
    );
  }

  if (shown.length === 0) {
    return (
      <Callout tone="warning" title="No affected pages recorded" icon={<AlertIcon className="h-5 w-5" />}>
        This recommendation does not list the pages it applies to, so there is nothing to write
        against. Re-run the audit and try again.
      </Callout>
    );
  }

  function submit() {
    const nextErrors: Record<string, string> = {};
    const payloads: Record<string, unknown>[] = [];

    for (const url of shown) {
      const value = valueFor(url);
      const error = validateValue(value, spec!);
      if (error) {
        nextErrors[url] = error;
        continue;
      }
      payloads.push({ url, before: '', after: value.trim() });
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) onSubmit(payloads);
  }

  return (
    <div className="space-y-4">
      {drafting ? (
        <p className="flex items-center gap-2 text-sm text-ink-muted">
          <Spinner className="h-4 w-4" />
          Writing values…
        </p>
      ) : (
        <div className="flex flex-wrap items-start justify-between gap-3">
          <p className="text-sm text-ink-muted">
            {hasDrafts
              ? 'Written for you and left editable. Creating the action still leaves it awaiting your approval — nothing is applied yet.'
              : 'Supply the text and it becomes an action awaiting your approval — nothing is applied to your site yet.'}
          </p>
          {onRedraft ? (
            <Button size="sm" variant="ghost" onClick={onRedraft} icon={<SparkIcon className="h-4 w-4" />}>
              {hasDrafts ? 'Rewrite' : 'Write with AI'}
            </Button>
          ) : null}
        </div>
      )}

      {draftNotes ? (
        <p className="rounded-lg bg-surface-muted px-3 py-2 text-xs leading-relaxed text-ink-muted">
          {draftNotes}
        </p>
      ) : null}

      <div className="space-y-3">
        {shown.map((url) => {
          const value = valueFor(url);
          const error = errors[url];
          const draft = drafts?.[url];
          const id = `value-${url}`;
          return (
            <div key={url}>
              <label htmlFor={id} className="block truncate font-mono text-xs text-ink-subtle" title={url}>
                {url}
              </label>
              {spec.multiline ? (
                <textarea
                  id={id}
                  rows={2}
                  value={value}
                  placeholder={spec.placeholder}
                  onChange={(event) => setEdits({ ...edits, [url]: event.target.value })}
                  className={`${inputClass} mt-1`}
                />
              ) : (
                <input
                  id={id}
                  value={value}
                  placeholder={spec.placeholder}
                  onChange={(event) => setEdits({ ...edits, [url]: event.target.value })}
                  className={`${inputClass} mt-1`}
                />
              )}
              <div className="mt-1 flex items-center justify-between gap-3 text-xs">
                {error ? (
                  <span className="text-danger">{spec.label} {error}</span>
                ) : (
                  <span className="text-ink-subtle">{spec.label}</span>
                )}
                <span
                  className={
                    value.trim().length > spec.maxLength ? 'tnum text-danger' : 'tnum text-ink-subtle'
                  }
                >
                  {value.trim().length}/{spec.maxLength}
                </span>
              </div>
              {draft?.justification ? (
                <p className="mt-1 flex items-start gap-1.5 text-[11px] leading-relaxed text-ink-subtle">
                  <SparkIcon className="mt-px h-3 w-3 shrink-0" />
                  {draft.justification}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>

      {urls.length > shown.length ? (
        <p className="text-xs text-ink-subtle">
          Showing the first {MAX_URLS} of {urls.length} pages. Create these, then repeat for the
          rest.
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button variant="primary" size="sm" loading={pending} onClick={submit}>
          Create {shown.length === 1 ? 'action' : `${shown.length} actions`}
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
